import { string, object, number, mixed, boolean } from 'yup';
import phone from 'phone';
import { allCountries } from 'country-region-data';

const phoneBase = (label, country, canBeNull) =>
	string()
		.test(`${label} test`, `\${value} is not a valid ${label} number`, (val) =>
			val ? validateNumber(val, country) : canBeNull,
		)
		.transform((x) => {
			try {
				if (x?.startsWith('+')) {
					return x;
				}
				const number = phone(x, { country, validateMobilePrefix: false });
				return number.isValid ? number.phoneNumber : x;
			} catch (error) {
				console.error('Error transforming phone number: ', error);
				return x;
			}
		});

function customFieldVerification(type, country) {
	switch (type) {
		case "Phone":
			return phoneBase("phone", country, true).nullable();
		case "String":
			return string().nullable();
		case "DateTime":
			return mixed().nullable().transform(value => {
				const number = Number(value);
				if (isNaN(number)) {
					return Date.parse(value);
				} else if (Number.isInteger(value)) {
					return number;
				} else {
					return NaN;
				}
			}).test({
				name: "DateTime test",
				message: context => `${context.originalValue} is not a valid DateTime string or millisecond epoch value`,
				test: (value, context) => {
					return !isNaN(value) || !context.originalValue;
				}
			});
		case "Boolean":
			return boolean().nullable().transform(x => {
				const lower = `${x}`.toLowerCase();
				if (lower === 'true' || lower === '1') {
					return true;
				} else {
					return false;
				}
			});
		case "Number":
			return number().nullable().typeError(context => `${context.originalValue} is not a valid number`);
		default:
			return string().nullable();
	}

}

const customFieldBaseSchema = {
	Phone: phoneBase("phone", true).nullable(),
	String: string().nullable(),
	DateTime: mixed().nullable().transform(value => {
		const number = Number(value);
		if (isNaN(number)) {
			return Date.parse(value);
		} else if (Number.isInteger(value)) {
			return number;
		} else {
			return NaN;
		}
	}).test({
		name: "DateTime test",
		message: context => `${context.originalValue} is not a valid DateTime string or millisecond epoch value`,
		test: (value, context) => {
			return !isNaN(value) || !context.originalValue;
		}
	}),
	Boolean: boolean().nullable().transform(x => {
		const lower = `${x}`.toLowerCase();
		if (lower === 'true' || lower === '1') {
			return true;
		} else {
			return false;
		}
	}),
	Number: number().nullable().typeError(context => `${context.originalValue} is not a valid number`)
};

const contactValidation = (customFields, country) => {
	// Get Regions(States, Provinces, etc) for the specified country
	const getCountryRegions = (code) => {
		const countryData = allCountries.find(
			(c) => c[0] === code || c[1] === code
		);
		return countryData?.[2] || [];
	};

	const US_REGIONS = getCountryRegions("US");
	const CA_REGIONS = getCountryRegions("CA");

	const buildStateMaps = (regions) => {
		const nameToCode = {};
		const validCodes = new Set();

		regions.forEach(([name, code]) => {
			const upperName = name.toUpperCase();
			const upperCode = code.toUpperCase();

			nameToCode[upperName] = upperCode;
			validCodes.add(upperCode);
		});

		return { nameToCode, validCodes };
	};

	const regions =
		!country || country === "US" || country === "CA"
			? [...US_REGIONS, ...CA_REGIONS]
			: getCountryRegions(country);

	const { nameToCode, validCodes } = buildStateMaps(regions);

	// --- Phone schemas ---
	const phoneOptional = phoneBase("phone", country, true).nullable();
	const cellOptional = phoneBase("cell phone", country, true).nullable();

	// --- Custom fields ---
	const customFieldShape = (customFields || []).reduce((acc, field) => {
		const base = customFieldVerification(field.type, country);

		acc[field.name] =
			field.type === "String" && field.isPoc
				? base.email("${value} is not a valid email")
				: base;

		return acc;
	}, {});

	// --- Main schema ---
	return object()
		.shape({
			firstName: string()
				.max(124, "First name must be 124 characters or less")
				.required("Please supply a first name"),

			lastName: string()
				.max(124, "Last name must be 124 characters or less")
				.required("Please supply a last name"),

			source: string()
				.nullable()
				.max(40, "Source must be 40 characters or less"),

			phone: phoneOptional,
			cell: cellOptional,

			address1: string()
				.nullable()
				.max(50, "Address1 must be 50 characters or less"),

			address2: string()
				.nullable()
				.max(50, "Address2 must be 50 characters or less"),

			city: string().nullable(),

			state: string()
				.nullable()
				.transform((value) => {
					if (!value) return value;

					const normalized = value.trim().toUpperCase();

					// Already a valid code
					if (validCodes.has(normalized)) {
						return normalized;
					}

					// Full name → convert to code
					if (nameToCode[normalized]) {
						return nameToCode[normalized];
					}

					return normalized; // let validation fail if invalid
				})
				.test(
					"valid-state",
					"${value} is an invalid state. If you believe this is an error, a country code may be required to validate the state field.",
					(val) => {
						if (!val) return true;
						return validCodes.has(val);
					}
				),

			zip: string()
				.nullable()
				.transform((val) => (val ? val.trim().toUpperCase() : val))
				.when([], {
					is: () => !country || country === "US",
					then: () => string().nullable().matches(
						/^\d{5}(-\d{4})?$/,
						{
							message: "${value} is not a valid ZIP code",
							excludeEmptyString: true,
						}
					),
					otherwise: () => string().nullable()
						.max(12, "Postal code is too long")
						.matches(/^[A-Z0-9 -]+$/, {
							message: "${value} is not a valid postal code",
							excludeEmptyString: true,
						}),
				}),

			email: string()
				.nullable()
				.email("${value} is not a valid email address"),

			customFields: object().shape(customFieldShape),
		})

		// --- Cross-field validation (phone OR cell required) ---
		.test(
			"phone-or-cell",
			"Either phone or cell phone is required",
			(values) => {
				if (!values) return false;
				return !!(values.phone || values.cell);
			}
		);
};

const segmentValidation = object({
	name: string()
		.max(50, 'Segment name must be 20 characters or less')
		.required('Please supply a segment name'),
});

const cxOneValidation = object({
	businessUnit: string()
		.matches(/^\d+$/, 'Must only contain numbers')
		.max(9, 'Must be at most 9 digits long')
		.min(7, 'Must be at least 7 digits long')
		.required('Business unit is required'),
	accessKey: string()
		.max(80, 'Must be 80 characters or less')
		.required('Access key is required'),
	appSecret: string()
		.max(80, 'Must be 80 characters or less')
		.required('Application secret is required'),
	authTokenUrl: string()
		.max(80, 'Must be 80 characters or less')
		.url('Must be a valid URL'),
});

export const webexCCValidation = object({
	region: string().required('Region is required'),
	organizationId: string()
		.max(80, 'Must be 80 characters or less')
		.required('Organization ID is required'),
	clientId: string()
		.max(80, 'Must be 80 characters or less')
		.required('Client ID is required'),
	clientSecret: string()
		.max(80, 'Must be 80 characters or less')
		.required('Client secret is required'),
});

export const webexConnectValidation = object({
	region: string().required('Region is required'),
	serviceKey: string().required('Service key is required'),
});

const inContactValidation = object({
	icUserName: string()
		.nullable()
		.max(50, 'Must be 20 characters or less')
		.required('Please supply an inContact User Name'),
	icPassword: string()
		.nullable()
		.max(48, 'Must be 48 characters or less')
		.required('Please supply an inContact Password'),
	icBU: string()
		.nullable()
		.max(7, 'The Business Unit Number is 7 Characters Long')
		.min(7, 'The Business Unit Number is 7 Characters Long')
		.required('Please supply a business unit number')
		.matches(/^\d{7}$/, 'Please supply a valid business unit number'),
	icApp: string()
		.nullable()
		.max(20, 'Please Make your application name less than 20 characters')
		.required('Please supply an application name'),
	icVendor: string()
		.nullable()
		.max(50, 'Please Make your application name less than 20 characters')
		.required('Please supply a vendor name'),
	icGetAuthTokenURL: string()
		.nullable()
		.required('This field is required')
		.url('Please enter a valid authentication token URL'),
});

function validateNumber(number, country) {
	try {
		// Check to see if the phone number starts with a + sign
		if (number.startsWith('+')) {
			// If it does, return the phone number as is
			return phone(number, { validateMobilePrefix: false }).isValid;
		} else {
			return phone(number, { validateMobilePrefix: false, country }).isValid;
		}
	} catch (error) {
		console.error('Error validating phone number: ', error);
		return false;
	}
}

// eslint-disable-next-line
export { segmentValidation, cxOneValidation, inContactValidation, contactValidation };
